import { faker } from '@faker-js/faker';
import { http, HttpResponse } from 'msw';

import { env } from '@/config/env';
import { axios } from '@/lib/axios';
import { MOCK_APEX_VARIANT_ID2, networkDelay } from '@/test/mocks/utils';

import type {
  UpcomingSession,
  UpcomingSessionsResponse,
  SessionErrorResponse,
} from '../types';

// ─── DEV: Session card variant mix ───────────────────────────────────
// Change this to test different combinations:
//   'first-only'  → 1st card Apex, 2nd card normal (default)
//   'both'        → both cards Apex
//   'none'        → both cards normal (no Apex styling)
//   'second-only' → 1st card normal, 2nd card Apex (logically consistent numbering)
type ApexSessionMix = 'first-only' | 'both' | 'none' | 'second-only';
function getApexSessionMix(): ApexSessionMix {
  // 'both' → both sessions are tied to the Apex trial course, so the trial tag
  // (with its session number) renders on the upcoming session cards.
  return 'both';
}

// ─── DEV: Number of upcoming sessions to return ──────────────────────
// Change this to exercise the different UpcomingSessions states:
//   2 → two session cards (default)
//   1 → one session card + "More sessions coming up soon" placeholder
//   0 → no sessions → "No upcoming sessions yet" empty state
// NOTE: the *second* card is also fed by extra classes. To truly see the
// 1-session / 0-session empty states, also set EXTRA_CLASSES_ENABLED = false
// in `extra-classes.ts` (otherwise a hobby/master class fills the slot).
type UpcomingSessionCount = 0 | 1 | 2;
function getUpcomingSessionCount(): UpcomingSessionCount {
  // 2 → render the upcoming session cards so the trial tag (and its session
  // number) is visible. Set to 0 to exercise the "trial completed" card instead.
  return 2;
}

// The trial tag derives from the enrolled course's is_trial (profiles mock),
// so tying the variant_id is all that's needed here.
const applyApexToSession = (session: UpcomingSession) => {
  session.variant_id = MOCK_APEX_VARIANT_ID2;
};

export const getUpcomingSessions = (): Promise<UpcomingSessionsResponse> => {
  return axios.get('/user/v2/upcoming-session?include_cancelled=true');
};

function* generateUpcomingSessions(
  count: number = 2,
  baseTimes?: number[]
): Generator<UpcomingSession> {
  for (let i = 0; i < count; i++) {
    const baseTime = baseTimes?.[i] ?? Date.now() + (i + 1) * 24 * 3_600_000; // Use provided base time or default
    const sessionDate = new Date(baseTime);

    // Round to the nearest hour (set minutes, seconds, milliseconds to 0)
    sessionDate.setMinutes(0, 0, 0);

    yield {
      session_type: faker.helpers.arrayElement([
        'Batch Session',
        'Demo Session',
        'Master Class',
      ]),
      session_id: faker.string.uuid(),
      upcoming: true,
      number: i + 1,
      batch_allocated: true, // Set to true to ensure sessions show
      timestamp: sessionDate.getTime(),
      timezone: 'Asia/Kolkata',
      video: faker.internet.url(),
      delivery_type: faker.helpers.arrayElement(['offline']),
      input_stream: faker.internet.url(),
      input_stream_duration: faker.number.int({ min: 30, max: 120 }).toString(),
      assessment_room_id: faker.string.alphanumeric(10),
      platform: faker.helpers.arrayElement(['100ms', 'ivs']),
      link: faker.internet.url(),
      room_id: faker.string.alphanumeric(8),
      readable_date: sessionDate.toLocaleDateString('en-US', {
        day: 'numeric',
        month: 'short',
      }),
      readable_time: sessionDate.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      }),
      session_status: faker.helpers.arrayElement(['cancelled']),
      comments_category: faker.helpers.arrayElement([
        'general',
        'technical',
        'other',
      ]),
      comments: faker.lorem.sentence(),
      readable_day: sessionDate.toLocaleDateString('en-US', {
        weekday: 'long',
      }),
      remaining_seconds: 90_000, // 25 hours to make it clearly upcoming
      payment_link: null, // No payment link for regular sessions
      trial: null, // Will be set by trial logic
      allow_session_join: true, // Allow joining for testing
      subject: faker.helpers.arrayElement([
        'Math',
        'Advanced Math',
        'Problem Solving',
      ]),
      variant_id: faker.string.uuid(), // Will be overridden by Apex logic
    };
  }
}

export const upcomingSessionMockHandler = http.get(
  `${env.API_URL}/user/v2/upcoming-session`,
  async (): Promise<
    HttpResponse<UpcomingSessionsResponse | SessionErrorResponse>
  > => {
    await networkDelay();

    // Sessions exist independently of payment status — payment status only
    // affects payment banners, not session availability.
    const sessionCount = getUpcomingSessionCount();

    // No upcoming sessions → backend returns a FAILURE shape; the UI renders
    // the neutral "No upcoming sessions yet" empty state.
    if (sessionCount === 0) {
      return HttpResponse.json(
        {
          status: 'FAILURE',
          message: 'No upcoming session',
          exception: 'application-exception',
        },
        { status: 200 }
      );
    }

    // Predictable base times: 30 minutes and 90 minutes from now.
    const now = Date.now();
    const baseTimes = [now + 1800 * 1000, now + (1800 + 3600) * 1000];

    const randomizedSessions = [
      ...generateUpcomingSessions(sessionCount, baseTimes),
    ];

    const sessionMix = getApexSessionMix();

    // Ensure session numbers are sequential and logical
    for (const [index, session] of randomizedSessions.entries()) {
      session.number = index + 1;
    }

    // DEV: the "trial completed" card is now gated on the selected course being
    // on trial with no payment due, NO upcoming sessions, and 0 remaining
    // credits. To exercise it, set `getUpcomingSessionCount()` to 0 above and
    // give the selected trial course `current_credit` totalling 0 in the
    // profiles mock (`get-profiles.ts`).

    if (
      (sessionMix === 'first-only' || sessionMix === 'both') &&
      randomizedSessions[0]
    ) {
      applyApexToSession(randomizedSessions[0]);
    }

    if (
      (sessionMix === 'both' || sessionMix === 'second-only') &&
      randomizedSessions[1]
    ) {
      applyApexToSession(randomizedSessions[1]);
    }

    const response: UpcomingSessionsResponse = {
      sessions: randomizedSessions,
      status: 'success',
      message: `Found ${sessionCount} upcoming session${sessionCount > 1 ? 's' : ''}`,
      batch_allocated: faker.datatype.boolean(),
      upcoming: true,
    };

    return HttpResponse.json(response);
  }
);
